import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

type SupportedPlatform = 'ios' | 'android' | 'web';

interface VersionRuleRow {
  min_version: string;
  link: string;
  platform: SupportedPlatform | 'all';
  is_active: boolean;
  created_at: string;
}

export interface ActiveVersionRule {
  minVersion: string;
  link: string;
  platform: SupportedPlatform | 'all';
}

const sanitizeVersion = (value: string | null | undefined): number[] => {
  if (!value) {
    return [0];
  }

  const cleaned = value.trim().replace(/[^0-9.]/g, '');
  if (!cleaned) {
    return [0];
  }

  return cleaned
    .split('.')
    .map((part) => Number.parseInt(part, 10))
    .map((part) => (Number.isNaN(part) ? 0 : part));
};

export const compareVersions = (current: string, required: string): number => {
  const currentParts = sanitizeVersion(current);
  const requiredParts = sanitizeVersion(required);
  const length = Math.max(currentParts.length, requiredParts.length);

  for (let i = 0; i < length; i += 1) {
    const currentPart = currentParts[i] ?? 0;
    const requiredPart = requiredParts[i] ?? 0;

    if (currentPart > requiredPart) {
      return 1;
    }
    if (currentPart < requiredPart) {
      return -1;
    }
  }

  return 0;
};

export const getDevicePlatform = (): SupportedPlatform => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    return platform;
  }

  return 'web';
};

export const getDeviceAppVersion = async (): Promise<string> => {
  if (Capacitor.isNativePlatform()) {
    try {
      const info = await App.getInfo();
      if (info.version) {
        return info.version;
      }
    } catch {
      // Fall back to env-based version.
    }
  }

  return import.meta.env.VITE_APP_VERSION ?? '0.0.0';
};

export const fetchActiveVersionRule = async (supabaseClient: unknown): Promise<ActiveVersionRule | null> => {
  const platform = getDevicePlatform();

  const { data, error } = await (supabaseClient as any)
    .from('app_version_requirements')
    .select('min_version, link, platform, is_active, created_at')
    .eq('is_active', true)
    .in('platform', ['all', platform])
    .order('created_at', { ascending: false })
    .limit(20);

  if (error || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const rows = data as VersionRuleRow[];
  const exact = rows.find((row) => row.platform === platform);
  const fallback = rows.find((row) => row.platform === 'all');
  const winner = exact ?? fallback ?? rows[0];

  return {
    minVersion: winner.min_version,
    link: winner.link,
    platform: winner.platform,
  };
};
